/**
 * BuildingStack3DViewer — V14.63 Sprint 26F (vanilla three.js rewrite).
 * See `useThreeViewer.js` for the rationale.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import {
  WALL_COLORS, TERRAIN_COLOR,
  computeCentroidPx, polygonToShape, boundsPx,
  buildStackLayout, resolveTransform, applyTransform,
  effectiveWallHeight, buildGuardrail,
  buildVirtualWallLine,
} from './geomUtils';
import { useThreeViewer, disposeGroup } from './useThreeViewer';
import { addSatelliteGroundPlane } from './SatelliteGroundPlane';

const buildFloorLayer = ({ floor, stackInfo, transform, scaleMPerPx, cx, cy, isActive, dim }) => {
  const layer = new THREE.Group();
  const walls = floor?.geometry?.walls || [];
  const terrain = floor?.geometry?.terrain || [];
  const skewEdges = floor?.geometry?.skew_edges || [];  // Sprint 31 Phase 2
  const veluxes = floor?.geometry?.velux || [];
  const height = stackInfo.height;
  const bottomY = stackInfo.bottom_z;
  const opacity = dim ? 0.35 : 1;

  if (floor.level === 0 && terrain.length >= 3) {
    const transformedPolygon = terrain.map((v) => {
      const x = Array.isArray(v) ? v[0] : v?.x;
      const y = Array.isArray(v) ? v[1] : v?.y;
      const p = applyTransform(x || 0, y || 0, transform);
      return { x: p.x, y: p.y };
    });
    const tShape = polygonToShape({ polygon: transformedPolygon, scaleMPerPx, cx, cy });
    if (tShape) {
      const mesh = new THREE.Mesh(
        new THREE.ShapeGeometry(tShape),
        new THREE.MeshStandardMaterial({
          color: TERRAIN_COLOR, roughness: 0.95, transparent: true, opacity: opacity * 0.95,
        }),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = bottomY - 0.04;
      mesh.receiveShadow = true;
      layer.add(mesh);
    }
  }

  for (const w of walls) {
    const p1 = applyTransform(w.x1, w.y1, transform);
    const p2 = applyTransform(w.x2, w.y2, transform);
    const mx1 = (p1.x - cx) * scaleMPerPx;
    const mz1 = (p1.y - cy) * scaleMPerPx;
    const mx2 = (p2.x - cx) * scaleMPerPx;
    const mz2 = (p2.y - cy) * scaleMPerPx;
    const dx = mx2 - mx1, dz = mz2 - mz1;
    const length = Math.hypot(dx, dz);
    if (length < 0.01) continue;
    if (w.kind === 'mur_invisible') { // V14.96 — limite virtuelle : pointillé au sol
      const line = buildVirtualWallLine(THREE, { x1: mx1, z1: mz1, x2: mx2, z2: mz2, y: bottomY + 0.01 });
      line.material.opacity = Math.min(1, opacity + 0.2);
      layer.add(line);
      continue;
    }
    const thicknessM = Math.max(0.05, (w.thickness || 8) * scaleMPerPx);
    // Sprint 30 — Mansarde : si le mur porte un `height_override_m` inférieur
    // à la HSP de l'étage, on l'affiche à cette hauteur (le mur bas d'un
    // rampant). Le mur reste posé sur le sol (bottomY), pas suspendu.
    const wallHeight = effectiveWallHeight(w, height); // V14.91 — cloison basse 1,10 m
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(length, wallHeight, thicknessM),
      new THREE.MeshStandardMaterial({
        color: WALL_COLORS[w.kind] || WALL_COLORS.default,
        roughness: 0.7, transparent: true, opacity,
        emissive: isActive ? 0x10b981 : 0x000000,
        emissiveIntensity: isActive ? 0.08 : 0,
      }),
    );
    mesh.position.set((mx1 + mx2) / 2, bottomY + wallHeight / 2, (mz1 + mz2) / 2);
    mesh.rotation.y = -Math.atan2(dz, dx);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    layer.add(mesh);
    if (w.kind === 'cloison_basse' && wallHeight < height - 0.3) {
      const rail = buildGuardrail(THREE, { length, baseY: bottomY + wallHeight, style: w.railing_style, thickness: thicknessM });
      rail.position.set((mx1 + mx2) / 2, 0, (mz1 + mz2) / 2);
      rail.rotation.y = -Math.atan2(dz, dx);
      layer.add(rail);
    }
  }

  // Sprint 31 Phase 2 — Mansarde roof facet(s).
  // For each skew_edge, add a semi-transparent tilted plane going from
  // the skew line (at height `hsp_m`) DOWN to the low side. We infer the
  // low side by picking the perpendicular direction that points AWAY from
  // the floor centroid — i.e. towards the exterior of the building.
  for (const e of skewEdges) {
    const p1 = applyTransform(e.x1, e.y1, transform);
    const p2 = applyTransform(e.x2, e.y2, transform);
    const mx1 = (p1.x - cx) * scaleMPerPx;
    const mz1 = (p1.y - cy) * scaleMPerPx;
    const mx2 = (p2.x - cx) * scaleMPerPx;
    const mz2 = (p2.y - cy) * scaleMPerPx;
    const dxE = mx2 - mx1, dzE = mz2 - mz1;
    const len = Math.hypot(dxE, dzE);
    if (len < 0.1) continue;
    const midX = (mx1 + mx2) / 2, midZ = (mz1 + mz2) / 2;
    // Perpendicular unit vector — pick the one that points AWAY from origin
    // (assumed centroid). This is the "low side" of the mansarde.
    let nx = -dzE / len, nz = dxE / len;
    if (nx * midX + nz * midZ < 0) { nx = -nx; nz = -nz; }
    // Rampant extends 2.5 m out from the skew line (typical eaves reach).
    const REACH_M = 2.5;
    const lowHsp = Number.isFinite(e.low_side_hsp_m) && e.low_side_hsp_m > 0
      ? Math.min(e.low_side_hsp_m, height) : Math.min(1.2, height);
    // 4 corners of the sloped facet — build a Shape then extrude/no; we
    // use raw BufferGeometry with 2 triangles for simplicity.
    // High side (at skew line, height = hsp)
    const A = [mx1, bottomY + height, mz1];
    const B = [mx2, bottomY + height, mz2];
    // Low side (at rampant edge, height = lowHsp)
    const C = [mx2 + nx * REACH_M, bottomY + lowHsp, mz2 + nz * REACH_M];
    const D = [mx1 + nx * REACH_M, bottomY + lowHsp, mz1 + nz * REACH_M];
    const geom = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      ...A, ...B, ...C,   // tri 1
      ...A, ...C, ...D,   // tri 2
    ]);
    geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geom.computeVertexNormals();
    const roof = new THREE.Mesh(
      geom,
      new THREE.MeshStandardMaterial({
        color: 0xd97706,  // amber-600 = tuiles
        roughness: 0.85, metalness: 0.05,
        transparent: true, opacity: 0.55,
        side: THREE.DoubleSide,
        emissive: isActive ? 0xf59e0b : 0x000000,
        emissiveIntensity: isActive ? 0.15 : 0,
      }),
    );
    roof.receiveShadow = true;
    roof.castShadow = true;
    roof.userData = { skewId: e.id, A, B, C, D, nx, nz, low_hsp: lowHsp };
    layer.add(roof);

    // Sprint 31 Phase 2 — Velux placés sur cette mansarde. Position sur
    // la surface via bilinear interp (t le long AB, offset perpendiculaire
    // au milieu du rampant).
    const vsHere = veluxes.filter((v) => v.skewId === e.id);
    for (const v of vsHere) {
      // Midway between skew line and eaves (natural velux position).
      const OFFSET = 0.5;  // 0=on ridge, 1=on eaves
      const t = v.t;
      const px = mx1 + t * dxE + nx * REACH_M * OFFSET;
      const pz = mz1 + t * dzE + nz * REACH_M * OFFSET;
      const py = bottomY + height + (lowHsp - height) * OFFSET;
      const vGeom = new THREE.BoxGeometry(v.width_m, 0.06, v.height_m);
      const vMesh = new THREE.Mesh(
        vGeom,
        new THREE.MeshStandardMaterial({
          color: 0x0ea5e9, transparent: true, opacity: 0.85,
          roughness: 0.15, metalness: 0.55,
          emissive: 0x38bdf8, emissiveIntensity: isActive ? 0.35 : 0.15,
        }),
      );
      // Rotate to match roof pitch: rotate around the skew line axis.
      const pitch = Math.atan2(height - lowHsp, REACH_M);
      vMesh.rotation.y = -Math.atan2(dzE, dxE);
      vMesh.rotation.x = pitch;
      vMesh.position.set(px, py, pz);
      vMesh.castShadow = true;
      layer.add(vMesh);
    }
  }
  return layer;
};

const BuildingStack3DViewer = ({
  floors = [],
  activeFloorId = null,
  showGrid = true,
  background = '#0b1220',
  className = '',
  testId = 'building-stack-3d-viewer',
  projectId = null,        // Sprint 32 — satellite compositing
  showSatellite = false,   // Sprint 32 — user-toggled
  satelliteAlignment = null, // Sprint 32.5 — {rotation_deg, offset_x_m, offset_z_m}
  explodedGap = 0,         // Sprint 34 — meters of vertical gap between floors (0 = normal)
}) => {
  const layout = useMemo(() => {
    if (!floors.length) return null;
    const stack = buildStackLayout(floors);
    const sortedByWalls = [...floors].sort((a, b) =>
      (b.geometry?.walls?.length || 0) - (a.geometry?.walls?.length || 0));
    const referenceFloor = sortedByWalls[0];
    let scaleMPerPx = referenceFloor?.geometry?.scale_m_per_px;
    if (!(scaleMPerPx > 0)) {
      for (const f of floors) {
        if (f?.geometry?.scale_m_per_px > 0) { scaleMPerPx = f.geometry.scale_m_per_px; break; }
      }
    }
    if (!(scaleMPerPx > 0)) scaleMPerPx = 0.035;
    let cx = 0, cy = 0;
    if (referenceFloor) {
      const t = resolveTransform(referenceFloor);
      const walls = referenceFloor.geometry?.walls || [];
      const terrain = referenceFloor.geometry?.terrain || [];
      const tw = walls.map((w) => {
        const p1 = applyTransform(w.x1, w.y1, t);
        const p2 = applyTransform(w.x2, w.y2, t);
        return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
      });
      const tt = terrain.map((v) => {
        const x = Array.isArray(v) ? v[0] : v?.x;
        const y = Array.isArray(v) ? v[1] : v?.y;
        const p = applyTransform(x || 0, y || 0, t);
        return { x: p.x, y: p.y };
      });
      const c = computeCentroidPx(tw, tt);
      cx = c.cx; cy = c.cy;
    }
    const bb = referenceFloor
      ? boundsPx(referenceFloor.geometry?.walls || [], referenceFloor.geometry?.terrain || [])
      : { w: 0, h: 0 };
    const wM = Math.max(4, bb.w * scaleMPerPx);
    const dM = Math.max(4, bb.h * scaleMPerPx);
    const minBottom = Object.values(stack).reduce((acc, s) => Math.min(acc, s.bottom_z), 0);
    const maxTop    = Object.values(stack).reduce((acc, s) => Math.max(acc, s.top_z), 0);
    const totalH = Math.max(0.1, maxTop - minBottom);
    const radius = Math.max(wM, dM, totalH) * 1.3;
    return { stack, scaleMPerPx, cx, cy, radius, totalH, minBottom };
  }, [floors]);

  const camPos = useMemo(() =>
    layout ? [layout.radius, layout.radius * 0.7, layout.radius] : [12, 12, 12],
    [layout]);
  const target = useMemo(() =>
    layout ? [0, layout.totalH / 2 + layout.minBottom, 0] : [0, 0, 0],
    [layout]);

  const { canvasRef } = useThreeViewer({
    background,
    cameraInit: { pos: camPos, target },
    enableShadows: true,
    deps: [floors, activeFloorId, showGrid, layout, showSatellite, projectId, satelliteAlignment?.rotation_deg, satelliteAlignment?.offset_x_m, satelliteAlignment?.offset_z_m, explodedGap],
    onCreate: ({ scene }) => {
      if (!layout) return undefined;
      const root = new THREE.Group();
      const { stack, scaleMPerPx, cx, cy } = layout;
      // Sprint 34 — sort floors by level so we can offset each one progressively
      const sortedFloors = [...floors].sort((a, b) => (a.level || 0) - (b.level || 0));
      sortedFloors.forEach((f, idx) => {
        const stackInfo = stack[f.floor_id];
        if (!stackInfo) return;
        const t = resolveTransform(f);
        const layer = buildFloorLayer({
          floor: f, stackInfo, transform: t, scaleMPerPx, cx, cy,
          isActive: f.floor_id === activeFloorId,
          dim: activeFloorId && f.floor_id !== activeFloorId,
        });
        // Sprint 34 — explode: push each floor up by (gap * its level index)
        if (explodedGap > 0) {
          layer.position.y += explodedGap * idx;
        }
        root.add(layer);
      });
      if (showGrid) {
        const grid = new THREE.GridHelper(layout.radius * 2, Math.max(20, Math.floor(layout.radius * 2)),
          0x374151, 0x1f2937);
        grid.position.y = layout.minBottom - 0.02;
        root.add(grid);
      }
      const ambient = new THREE.AmbientLight(0xffffff, 0.6);
      const dir = new THREE.DirectionalLight(0xffffff, 1.0);
      dir.position.set(18, 28, 12);
      dir.castShadow = true;
      root.add(ambient);
      root.add(dir);
      scene.add(root);
      // Sprint 32 — satellite ground plane just below floors
      const cleanupSat = (showSatellite && projectId)
        ? addSatelliteGroundPlane({
            scene,
            projectId,
            radiusM: layout.radius * 1.15,
            groundY: layout.minBottom - 0.015,
            opacity: 0.9,
            rotationDeg: satelliteAlignment?.rotation_deg || 0,
            offsetXM: satelliteAlignment?.offset_x_m || 0,
            offsetZM: satelliteAlignment?.offset_z_m || 0,
          })
        : null;
      return () => {
        if (cleanupSat) cleanupSat();
        disposeGroup(root);
      };
    },
  });

  if (!floors.length) {
    return (
      <div className={`flex items-center justify-center text-gray-400 text-[12px] ${className}`}
           style={{ background, minHeight: 320 }}
           data-testid={testId}>
        Aucun étage à empiler — dessinez des murs sur au moins un étage.
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ background }} data-testid={testId}>
      <canvas ref={canvasRef}
              className="block w-full h-full"
              data-testid={`${testId}-canvas`} />
    </div>
  );
};

export default BuildingStack3DViewer;
